import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/supabaseClient';
import { attendanceUiText } from '@/lib/nfcUiVisibility';
import { hasCap } from '@/lib/capabilities';
import { useEffectiveMe } from '@/lib/effectiveMe';
import SettingsLayout from '@/components/settings/SettingsLayout';
import './attendanceQrAdmin.css';

const BASE = '/api/attendance/qr';
const panel = 'rounded-2xl border border-stone-200 bg-white p-5';
const primary = 'min-h-11 rounded-xl bg-brand-green px-4 text-sm font-semibold text-white disabled:opacity-50';
const secondary = 'min-h-10 rounded-xl border border-stone-200 px-3 text-sm font-semibold text-stone-700 disabled:opacity-50';
const field = 'mt-1 min-h-11 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm text-stone-800';
const date = (value) => value ? new Date(value).toLocaleString('it-IT') : 'Mai';
const post = (path, body) => apiFetch(`${BASE}${path}`, { method: 'POST', body: JSON.stringify(body) });

function ErrorNotice({ error, retry }) {
  return error ? (
    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
      <p>{error}</p>
      {retry && <button type="button" className={`${secondary} mt-3`} onClick={retry}>Riprova</button>}
    </div>
  ) : null;
}

// The CEO selector is server-authoritative (Dashboard + apiFetch cookie/header).
// /api/me alone is not the mutable selector. Never infer a tenant from point data.
export default function AttendanceQrKioskAdmin() {
  const me = useEffectiveMe();
  const identityKey = JSON.stringify([me?.id, me?.is_global, me?.active_tenant_id, me?.tenant_ids, me?.effective_capabilities]);
  return <TenantAdmin key={identityKey} me={me} />;
}

function TenantAdmin({ me }) {
  const [context, setContext] = useState(null);
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let alive = true;
    const resolve = async () => {
      setContext(null);
      setError('');
      try {
        if (me?.is_global) {
          const current = await apiFetch('/api/ceo/tenant-context');
          if (!alive) return;
          if (!current?.active_tenant_id) { setContext({ id: null }); return; }
          const tenants = await apiFetch('/api/ceo/tenants');
          const tenant = tenants.find(item => item.id === current.active_tenant_id);
          if (!tenant || !(tenant.display_name || tenant.name)) throw new Error('Tenant non disponibile. Seleziona nuovamente il tenant dalla Dashboard.');
          if (alive) setContext({ id: tenant.id, name: tenant.display_name || tenant.name });
        } else {
          const ids = me?.tenant_ids || [];
          // MeOut exposes authorized IDs, not tenant names. Do not invent a
          // label or request the CEO-only tenant directory for tenant staff.
          if (alive) setContext({ id: ids.length === 1 ? ids[0] : null });
        }
      } catch (e) { if (alive) setError(e.message || 'Impossibile verificare il tenant.'); }
    };
    resolve();
    // Other-tab selector changes and returning from the Dashboard must fail closed
    // while checking the signed context again. No storage values are trusted here.
    const refresh = () => { setContext(null); setVersion(value => value + 1); };
    const storage = (event) => { if (event.key === 'restaurantos-ceo-tenant-context' || event.key === null) refresh(); };
    window.addEventListener('focus', refresh);
    window.addEventListener('storage', storage);
    return () => { alive = false; window.removeEventListener('focus', refresh); window.removeEventListener('storage', storage); };
  }, [me, version]);
  if (context?.id) return <ScopedAdmin key={`${context.id}:${version}`} me={me} tenant={context} />;
  return (
    <SettingsLayout eyebrow="Operatività" title="Punti di timbratura QR" className="qr-admin-page" testId="attendance-qr-kiosk-admin">
      {error ? <ErrorNotice error={error} retry={() => setVersion(value => value + 1)} />
        : !context ? <div role="status" aria-label="Verifica tenant" className="h-24 animate-pulse rounded-2xl bg-stone-100" />
          : <section className={panel} data-testid="qr-admin-tenant-required">
            <h2 className="font-display text-lg font-semibold text-brand-navy">Seleziona un tenant</h2>
            <p className="mt-2 text-sm text-stone-600">I punti QR sono disponibili solo nel contesto di un tenant. Nessun dato viene aggregato tra tenant.</p>
            <Link to="/foundation#tenant-clienti" className="mt-4 inline-flex min-h-11 items-center rounded-xl bg-brand-green px-4 text-sm font-semibold text-white">Seleziona tenant dalla Dashboard</Link>
          </section>}
    </SettingsLayout>
  );
}

function ScopedAdmin({ me, tenant }) {
  const canCorrect = hasCap(me, 'attendance.correct');
  const [points, setPoints] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState('');
  const [pairing, setPairing] = useState(null);
  const [deviceName, setDeviceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ company_id: '', location_id: '', name: '' });
  const [createError, setCreateError] = useState('');
  const [copied, setCopied] = useState(false);
  const selected = points.find((point) => point.id === selectedId);
  const canManage = canCorrect && selected?.can_manage;
  const scope = useRef(0);
  const deviceRequest = useRef(0);
  const listRequest = useRef(0);
  const mutationLock = useRef(false);
  const mounted = useRef(false);
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);

  const load = useCallback(async () => {
    const request = ++listRequest.current;
    setLoading(true);
    setListError('');
    setPoints([]);
    setLocations([]);
    setSelectedId(null);
    setDevices([]);
    setPairing(null);
    setShowCreate(false);
    try {
      const [rows, allowed] = await Promise.all([
        apiFetch(`${BASE}/points`),
        canCorrect ? apiFetch(`${BASE}/locations`) : Promise.resolve([]),
      ]);
      if (!mounted.current || request !== listRequest.current) return;
      setPoints(rows.filter(point => point.tenant_id === tenant.id));
      setLocations(allowed);
    } catch (error) {
      if (mounted.current && request === listRequest.current) setListError(error.message || 'Impossibile caricare i punti QR.');
    } finally {
      if (mounted.current && request === listRequest.current) setLoading(false);
    }
  }, [canCorrect, tenant.id]);

  const loadDevices = useCallback(async (id, generation) => {
    const request = ++deviceRequest.current;
    setDevicesLoading(true);
    setDevicesError('');
    try {
      const rows = await apiFetch(`${BASE}/kiosk/devices?point_id=${encodeURIComponent(id)}`);
      if (mounted.current && scope.current === generation && request === deviceRequest.current) {
        setDevices(rows.filter((device) => device.point_id === id));
      }
    } catch (error) {
      if (mounted.current && scope.current === generation && request === deviceRequest.current) {
        setDevicesError(error.message || 'Impossibile caricare i dispositivi.');
      }
    } finally {
      if (mounted.current && scope.current === generation && request === deviceRequest.current) setDevicesLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; scope.current += 1; listRequest.current += 1; deviceRequest.current += 1; };
  }, [load]);

  useEffect(() => {
    if (!selectedId || !dialogRef.current) return undefined;
    const dialog = dialogRef.current;
    if (dialog.showModal) dialog.showModal();
    else dialog.setAttribute('open', '');
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      if (dialog.close) dialog.close();
      triggerRef.current?.focus();
    };
  }, [selectedId]);

  const closePoint = () => {
    scope.current += 1;
    deviceRequest.current += 1;
    mutationLock.current = false;
    setSelectedId(null);
    setDevices([]);
    setPairing(null);
    setDeviceName('');
    setConfirmation(null);
    setActionError(null);
    setCopied(false);
    setBusy(false);
  };

  const selectPoint = (point, trigger) => {
    triggerRef.current = trigger || document.activeElement;
    const generation = ++scope.current;
    setSelectedId(point.id);
    setDevices([]);
    setPairing(null);
    setDeviceName('');
    setCopied(false);
    setActionError(null);
    setConfirmation(null);
    mutationLock.current = false;
    setBusy(false);
    loadDevices(point.id, generation);
  };

  const runAction = async (work, retry) => {
    if (mutationLock.current || !canManage) return;
    const generation = scope.current;
    const lock = {};
    mutationLock.current = lock;
    setBusy(true);
    setActionError(null);
    try {
      await work(generation);
    } catch (error) {
      if (mounted.current && scope.current === generation) {
        setActionError({ message: error.message || 'Operazione non riuscita.', retry });
      }
    } finally {
      if (mutationLock.current === lock) {
        mutationLock.current = false;
        if (mounted.current && scope.current === generation) setBusy(false);
      }
    }
  };

  const createPairing = () => {
    if (!deviceName.trim() || selected?.status !== 'ACTIVE') return;
    setPairing(null);
    setCopied(false);
    runAction(async (generation) => {
      const result = await post('/kiosk/pairing-codes', { point_id: selected.id, device_name: deviceName.trim() });
      if (mounted.current && scope.current === generation) setPairing(result);
    }, createPairing);
  };

  const confirmAction = () => {
    const action = confirmation;
    if (!action) return;
    runAction(async (generation) => {
      if (action.type === 'status') {
        const status = selected.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        await post(`/points/${encodeURIComponent(selected.id)}/status`, { status });
        if (mounted.current && scope.current === generation) {
          setPoints((rows) => rows.map((point) => point.id === selected.id
            ? { ...point, status, activation_pending: false, ...(status === 'INACTIVE' ? { active_devices: 0 } : {}) } : point));
        }
      } else {
        await post('/kiosk/revoke', { device_id: action.device.id });
        if (mounted.current && scope.current === generation) {
          setPoints((rows) => rows.map((point) => point.id === selected.id
            ? { ...point, active_devices: Math.max(0, point.active_devices - 1) } : point));
        }
      }
      if (mounted.current && scope.current === generation) {
        setConfirmation(null);
        setPairing(null);
        await loadDevices(selected.id, generation);
      }
    }, confirmAction);
  };

  const availableLocations = locations.filter(location =>
    (!location.tenant_id || location.tenant_id === tenant.id)
    && !points.some(point => String(point.location_id) === String(location.id)));
  const companies = [...new Map(availableLocations.map((location) => [String(location.company_id), {
    id: location.company_id, name: location.company_name,
  }])).values()];
  const companyLocations = availableLocations.filter((location) => String(location.company_id) === form.company_id);
  const chosenLocation = companyLocations.find((location) => String(location.id) === form.location_id);

  const createPoint = async (event) => {
    event?.preventDefault();
    if (!canCorrect || !chosenLocation || mutationLock.current) return;
    const generation = scope.current;
    const lock = {};
    mutationLock.current = lock;
    setBusy(true);
    setCreateError('');
    try {
      const result = await post('/points', {
        company_id: chosenLocation.company_id,
        location_id: chosenLocation.id,
        name: form.name.trim() || `Presenze ${chosenLocation.name}`,
      });
      if (!mounted.current || scope.current !== generation || result.tenant_id !== tenant.id) return;
      setPoints((rows) => [...rows.filter((point) => point.id !== result.id), result]);
      setShowCreate(false);
      setForm({ company_id: '', location_id: '', name: '' });
      selectPoint(result);
    } catch (error) {
      if (mounted.current && scope.current === generation) setCreateError(error.message || 'Punto QR non creato.');
    } finally {
      if (mutationLock.current === lock) {
        mutationLock.current = false;
        if (mounted.current && scope.current === generation) setBusy(false);
      }
    }
  };

  return (
    <SettingsLayout eyebrow="Operatività" title="Punti di timbratura QR"
      description="Gestisci i punti di timbratura e gli schermi fissi per ogni azienda e locale."
      testId="attendance-qr-kiosk-admin" className="qr-admin-page">
      <div className="qr-admin-content space-y-5" data-testid="qr-admin-content">
        <p className="text-sm font-semibold text-brand-navy"><span data-testid="qr-admin-tenant">{tenant.name ? `Tenant: ${attendanceUiText(tenant.name)}` : 'Contesto tenant autorizzato'}</span>
          {me?.is_global && <Link to="/foundation#tenant-clienti" className="ml-3 font-normal text-brand-green underline">Cambia tenant</Link>}</p>
        <section className={panel}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><h2 className="font-display text-lg font-semibold text-brand-navy">Punti QR</h2>
              <p className="mt-1 text-sm text-stone-500">Ogni schermo è associato a un solo punto.</p></div>
            {canCorrect && availableLocations.length > 0 && !loading && !listError && <button type="button" className={primary} onClick={() => { setShowCreate(!showCreate); setCreateError(''); }}>
              {showCreate ? 'Chiudi creazione' : 'Aggiungi punto QR'}
            </button>}
          </div>
          {canCorrect && !loading && !listError && !availableLocations.length && <p className="mt-4 rounded-xl bg-stone-50 p-4 text-sm text-stone-600">Tutti i locali di questo tenant hanno già un punto QR</p>}
          {showCreate && canCorrect && availableLocations.length > 0 && (
            <form onSubmit={createPoint} className="mt-5 space-y-4 rounded-xl bg-stone-50 p-4" aria-label="Aggiungi punto QR">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-sm font-semibold text-stone-700">Azienda
                  <select aria-label="Azienda" className={field} required value={form.company_id} onChange={(event) => {
                    setForm({ company_id: event.target.value, location_id: '', name: '' });
                  }}>
                    <option value="">Seleziona azienda</option>
                    {companies.map((company) => <option key={company.id} value={company.id}>{attendanceUiText(company.name)}</option>)}
                  </select>
                </label>
                <label className="text-sm font-semibold text-stone-700">Locale
                  <select aria-label="Locale" className={field} required disabled={!form.company_id} value={form.location_id}
                    onChange={(event) => {
                      const location = companyLocations.find((item) => String(item.id) === event.target.value);
                      setForm({ ...form, location_id: event.target.value, name: location ? `Presenze ${location.name}` : '' });
                    }}>
                    <option value="">Seleziona locale</option>
                    {companyLocations.map((location) => <option key={location.id} value={location.id}>{attendanceUiText(location.name)}</option>)}
                  </select>
                </label>
              </div>
              <label className="block text-sm font-semibold text-stone-700">Nome punto (facoltativo)
                <input className={field} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
              </label>
              {!locations.length && !loading && <p className="text-sm text-stone-500">Nessun locale disponibile per la creazione.</p>}
              <ErrorNotice error={createError} retry={() => createPoint()} />
              <button type="submit" className={primary} disabled={busy || !chosenLocation}>{busy ? 'Attendere…' : 'Crea punto'}</button>
            </form>
          )}
          <div className="mt-4"><ErrorNotice error={listError} retry={load} /></div>
          {loading ? <div className="mt-4 space-y-3" role="status" aria-label="Caricamento punti QR">
            {[1, 2, 3].map((key) => <div key={key} className="h-14 animate-pulse rounded-xl bg-stone-100" />)}
          </div> : !listError && (points.length ? (
            <>
            <ul className="qr-admin-cards mt-4 space-y-3" aria-label="Punti QR per locale">
              {points.map(point => <li key={point.id} data-testid="qr-point-card" className="rounded-xl border border-stone-200 p-4">
                <p className="text-xs font-semibold text-stone-600">{attendanceUiText(point.company_name)}</p>
                <p className="mt-1 text-sm text-stone-600">{attendanceUiText(point.location_name)}</p>
                <h3 className="mt-2 font-display text-lg font-semibold text-brand-navy">{attendanceUiText(point.name)}</h3>
                <span className={`mt-2 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${point.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-800' : 'bg-stone-100 text-stone-600'}`}>{point.status === 'ACTIVE' ? 'Attivo' : point.activation_pending ? 'Da attivare' : 'Inattivo'}</span>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex flex-wrap justify-between gap-2"><dt className="text-stone-500">Dispositivi attivi</dt><dd className="font-semibold text-stone-700">{point.active_devices}</dd></div>
                  <div className="flex flex-wrap justify-between gap-2"><dt className="text-stone-500">Ultima attività</dt><dd className="text-stone-700">{date(point.last_activity)}</dd></div>
                </dl>
                <button type="button" className={`${secondary} mt-4 w-full`} aria-label={`Gestisci ${attendanceUiText(point.name)}`} aria-haspopup="dialog"
                  onClick={(event) => selectPoint(point, event.currentTarget)}>{canCorrect && point.can_manage ? 'Gestisci' : 'Dettagli'}</button>
              </li>)}
            </ul>
            <div className="qr-admin-table mt-4">
              <table className="w-full text-left text-sm">
                <caption className="sr-only">Punti di timbratura per azienda e locale</caption>
                <thead className="border-b border-stone-200 text-xs text-stone-500">
                  <tr>{['Azienda', 'Locale', 'Nome', 'Stato', 'Dispositivi attivi', 'Ultima attività', ''].map((heading, index) =>
                    <th key={index} scope="col" className="px-3 py-3 font-semibold">{heading || <span className="sr-only">Dettaglio</span>}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {points.map((point) => (
                    <tr key={point.id} className={point.id === selectedId ? 'bg-emerald-50/60' : ''}>
                      <td className="px-3 py-4">{attendanceUiText(point.company_name)}</td>
                      <td className="px-3 py-4">{attendanceUiText(point.location_name)}</td>
                      <th scope="row" className="px-3 py-4 font-semibold text-brand-navy">{attendanceUiText(point.name)}</th>
                      <td className="px-3 py-4"><span className={`whitespace-nowrap rounded-full px-2 py-1 text-xs font-semibold ${point.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-800' : 'bg-stone-100 text-stone-600'}`}>{point.status === 'ACTIVE' ? 'Attivo' : point.activation_pending ? 'Da attivare' : 'Inattivo'}</span></td>
                      <td className="px-3 py-4">{point.active_devices}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-stone-500">{date(point.last_activity)}</td>
                      <td className="px-3 py-4"><button type="button" className={secondary}
                        aria-label={`Gestisci ${attendanceUiText(point.name)}`} aria-haspopup="dialog"
                        onClick={(event) => selectPoint(point, event.currentTarget)}>{canCorrect && point.can_manage ? 'Gestisci' : 'Dettagli'}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          ) : <div className="mt-5 rounded-xl bg-stone-50 p-6"><h3 className="font-semibold text-brand-navy">Nessun punto QR</h3><p className="mt-1 text-sm text-stone-500">I punti accessibili compariranno qui, organizzati per azienda e locale.</p></div>)}
        </section>

        {selected && (
          <dialog ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="qr-point-title" aria-describedby="qr-point-context" className="qr-admin-dialog"
            onCancel={(event) => { event.preventDefault(); closePoint(); }}>
            <div className="qr-admin-dialog-head flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p id="qr-point-context" className="text-xs text-stone-500">{attendanceUiText(selected.company_name)} · {attendanceUiText(selected.location_name)}</p>
                <h2 id="qr-point-title" className="mt-1 break-words font-display text-xl font-semibold text-brand-navy">{attendanceUiText(selected.name)}</h2>
              </div>
              <button autoFocus type="button" className={`${secondary} shrink-0`} onClick={closePoint}>Chiudi</button>
            </div>
            <div className="qr-admin-dialog-body space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="text-sm text-stone-600"><p className="font-semibold">{selected.status === 'ACTIVE' ? 'Attivo' : selected.activation_pending ? 'Da attivare' : 'Inattivo'}</p>
                <p className="mt-1">Dispositivi attivi: {selected.active_devices}</p><p className="mt-1">Ultima attività: {date(selected.last_activity)}</p></div>
              {canManage && <button type="button" className={secondary} disabled={busy}
                onClick={() => { setConfirmation({ type: 'status' }); setActionError(null); }}>
                {selected.status === 'ACTIVE' ? 'Disattiva punto' : selected.activation_pending ? 'Attiva punto' : 'Riattiva punto'}
              </button>}
            </div>
            {!canManage && <p className="text-sm text-stone-500">Accesso in sola lettura a questo punto.</p>}
            {canManage && selected.status === 'ACTIVE' && (
              <div className="rounded-xl bg-stone-50 p-4">
                <h3 className="font-semibold text-brand-navy">Attiva un dispositivo fisso</h3>
                <p className="mt-1 text-sm text-stone-600">Il codice è monouso e valido per 10 minuti. Il nome dello schermo viene impostato qui.</p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
                  <label className="flex-1 text-sm font-semibold text-stone-700">Nome dispositivo
                    <input className={field} value={deviceName} onChange={(event) => setDeviceName(event.target.value)} placeholder="Es. Schermo ingresso" />
                  </label>
                  <button type="button" className={primary} disabled={busy || !deviceName.trim()} onClick={createPairing}>Genera codice</button>
                </div>
              </div>
            )}
            {pairing && canManage && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="qr-kiosk-pairing-code">
                <p className="text-sm font-semibold text-emerald-900">Codice monouso · valido per 10 minuti</p>
                <code className="mt-2 block break-all rounded-lg bg-white p-3 text-sm text-emerald-950">{pairing.pairing_code}</code>
                <p className="mt-2 text-xs text-emerald-800">Punto: {attendanceUiText(pairing.point_name)}. Scade il {date(pairing.expires_at)}. Il codice non verrà mostrato di nuovo.</p>
                <button type="button" className={`${secondary} mt-3`} onClick={async () => {
                  const generation = scope.current;
                  try {
                    if (!navigator.clipboard) throw new Error('Copia non disponibile: seleziona e copia il codice manualmente.');
                    await navigator.clipboard.writeText(pairing.pairing_code);
                    if (scope.current === generation && mounted.current) setCopied(true);
                  } catch (error) {
                    if (scope.current === generation && mounted.current) setActionError({ message: error.message });
                  }
                }}>{copied ? 'Codice copiato' : 'Copia codice'}</button>
              </div>
            )}
            {confirmation && canManage && (
              <div role="alertdialog" aria-modal="false" aria-labelledby="qr-confirm-title" aria-describedby="qr-confirm-description" className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <h3 id="qr-confirm-title" className="font-semibold text-brand-navy">{confirmation.type === 'status'
                  ? (selected.status === 'ACTIVE' ? 'Disattivare questo punto?' : selected.activation_pending ? 'Attivare questo punto?' : 'Riattivare questo punto?')
                  : 'Revocare questo dispositivo?'}</h3>
                <p id="qr-confirm-description" className="mt-2 text-sm text-stone-700">{confirmation.type === 'status'
                  ? (selected.status === 'ACTIVE' ? 'Tutti gli abbinamenti, i dispositivi e i QR non utilizzati saranno revocati. Gli schermi dovranno essere associati di nuovo.' : 'Il punto potrà ricevere nuovi dispositivi. I vecchi abbinamenti non saranno ripristinati.')
                  : `${attendanceUiText(confirmation.device.device_name)} non potrà più visualizzare i codici QR.`}</p>
                <div className="mt-4 flex gap-3">
                  <button type="button" className={primary} disabled={busy} onClick={confirmAction}>Conferma</button>
                  <button type="button" className={secondary} disabled={busy} onClick={() => { setConfirmation(null); setActionError(null); }}>Annulla</button>
                </div>
              </div>
            )}
            <ErrorNotice error={actionError?.message} retry={actionError?.retry} />
            <div>
              <h3 className="font-display text-lg font-semibold text-brand-navy">Dispositivi autorizzati</h3>
              <p className="mt-1 text-xs text-stone-500">Solo i dispositivi di questo punto.</p>
              <div className="mt-3"><ErrorNotice error={devicesError} retry={() => loadDevices(selected.id, scope.current)} /></div>
              {devicesLoading ? <div role="status" aria-label="Caricamento dispositivi" className="mt-3 h-16 animate-pulse rounded-xl bg-stone-100" />
                : !devicesError && (devices.length ? <ul className="mt-3 divide-y divide-stone-100">
                  {devices.map((device) => <li key={device.id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid="qr-kiosk-device">
                    <div><p className="text-sm font-semibold text-stone-800">{attendanceUiText(device.device_name)}</p>
                      <p className="mt-1 text-xs text-stone-500">{device.status === 'ACTIVE' ? 'Attivo' : 'Revocato'} · Scade il {date(device.expires_at)}</p>
                      <p className="mt-1 text-xs text-stone-500">Associato il {date(device.created_at)} · Ultimo accesso: {date(device.last_seen_at)}</p></div>
                    {canManage && device.status === 'ACTIVE' && <button type="button" className="min-h-10 rounded-xl border border-red-200 px-3 text-sm font-semibold text-red-700 disabled:opacity-50"
                      disabled={busy} onClick={() => { setConfirmation({ type: 'revoke', device }); setActionError(null); }}>Revoca</button>}
                  </li>)}
                </ul> : <p className="mt-3 rounded-xl bg-stone-50 p-4 text-sm text-stone-500">Nessun dispositivo associato a questo punto.</p>)}
            </div>
            <p className="text-sm text-stone-600">Sullo schermo fisso apri <Link className="font-semibold text-brand-green underline" to="/attendance-qr-kiosk">/attendance-qr-kiosk</Link> e inserisci il codice monouso.</p>
            </div>
          </dialog>
        )}
        <p className="text-sm text-stone-600">Sullo schermo fisso apri <Link className="font-semibold text-brand-green underline" to="/attendance-qr-kiosk">/attendance-qr-kiosk</Link>. Inserisci il codice generato per il punto scelto: non occorre selezionare il locale sul dispositivo.</p>
      </div>
    </SettingsLayout>
  );
}
